import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import api from '../../services/unified';
import type { ApplicationAnalytics } from '../../services/unified/types';

type StatusFilter = 'all' | 'submitted' | 'reviewed' | 'interviewing' | 'offered' | 'hired' | 'rejected' | 'withdrawn';

const STATUS_COLORS: Record<string, string> = {
  submitted: 'bg-blue-100 text-blue-800',
  reviewed: 'bg-yellow-100 text-yellow-800',
  interviewing: 'bg-purple-100 text-purple-800',
  offered: 'bg-green-100 text-green-800',
  hired: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  withdrawn: 'bg-gray-100 text-gray-800',
};

export function AdminApplicationsPage() {
  const { isMobile } = useViewport();
  const [analytics, setAnalytics] = useState<ApplicationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedApplications, setSelectedApplications] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const data = await api.analytics.getApplicationAnalytics();
      setAnalytics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };

  const filteredApplications = useMemo(() => {
    if (!analytics?.recent_applications) return [];
    
    let filtered = analytics.recent_applications;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(app => 
        app.id.toLowerCase().includes(query) ||
        app.job_id.toLowerCase().includes(query)
      );
    }
    
    return filtered;
  }, [analytics, statusFilter, searchQuery]);

  const handleSelectAll = () => {
    if (selectedApplications.size === filteredApplications.length) {
      setSelectedApplications(new Set());
    } else {
      setSelectedApplications(new Set(filteredApplications.map(app => app.id)));
    }
  };

  const handleSelectApplication = (id: string) => {
    const newSelected = new Set(selectedApplications);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedApplications(newSelected);
  };

  const handleBulkStatusUpdate = async (status: string) => {
    if (selectedApplications.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      await api.applications.bulkApplicationAction(
        Array.from(selectedApplications),
        'update_status',
        { status }
      );
      setSelectedApplications(new Set());
      await fetchAnalytics();
    } catch (err) {
      console.error('Bulk update failed:', err);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const statusCounts = analytics?.applications_by_status || {};
  const totalApplications = analytics?.total_applications || 0;

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="grid grid-cols-4 gap-4 mb-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load applications</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Applications</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage all job applications across your platform</p>
        </div>

        {/* Status Summary Cards - Horizontal scroll on mobile */}
        <div className="mb-4 sm:mb-6 -mx-4 sm:mx-0 px-4 sm:px-0">
          <div className="flex sm:grid sm:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-4 overflow-x-auto pb-2 sm:pb-0 mobile-scroll">
            <button
              onClick={() => setStatusFilter('all')}
              className={`flex-shrink-0 p-3 sm:p-4 rounded-lg border-2 transition-colors min-w-[80px] sm:min-w-0 min-h-[60px] ${
                statusFilter === 'all' 
                  ? 'border-primary-500 bg-primary-50' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="text-lg sm:text-2xl font-bold text-gray-900">{totalApplications}</div>
              <div className="text-xs sm:text-sm text-gray-600">All</div>
            </button>
            
            {(['submitted', 'reviewed', 'interviewing', 'offered', 'hired', 'rejected'] as const).map(status => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`flex-shrink-0 p-3 sm:p-4 rounded-lg border-2 transition-colors min-w-[80px] sm:min-w-0 min-h-[60px] ${
                  statusFilter === status 
                    ? 'border-primary-500 bg-primary-50' 
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="text-lg sm:text-2xl font-bold text-gray-900">
                  {statusCounts[status] || 0}
                </div>
                <div className="text-xs sm:text-sm text-gray-600 capitalize truncate">{status}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Bulk Actions */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-4 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
              <div className="flex-1 w-full sm:max-w-md">
                <input
                  type="text"
                  placeholder="Search by application ID or job ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              
              {selectedApplications.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {selectedApplications.size} selected
                  </span>
                  <select
                    onChange={(e) => {
                      if (e.target.value) {
                        handleBulkStatusUpdate(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    disabled={bulkActionLoading}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                  >
                    <option value="">Bulk Update Status...</option>
                    <option value="reviewed">Mark as Reviewed</option>
                    <option value="interviewing">Move to Interviewing</option>
                    <option value="offered">Mark as Offered</option>
                    <option value="hired">Mark as Hired</option>
                    <option value="rejected">Reject</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Applications - Card layout on mobile, table on desktop */}
          {isMobile ? (
            <div className="divide-y divide-gray-200">
              {filteredApplications.length === 0 ? (
                <div className="px-4 py-12 text-center text-gray-500">
                  No applications found
                </div>
              ) : (
                filteredApplications.map((app) => (
                  <div key={app.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedApplications.has(app.id)}
                        onChange={() => handleSelectApplication(app.id)}
                        className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500 min-w-[20px] min-h-[20px]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-800'}`}>
                            {app.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '-'}
                          </span>
                        </div>
                        <div className="text-sm font-medium text-gray-900 mb-1">
                          App: {app.id.slice(0, 8)}...
                        </div>
                        <div className="text-sm text-gray-500 mb-2">
                          Candidate: {app.user_id.slice(0, 8)}...
                        </div>
                        <div className="flex items-center justify-between">
                          <Link 
                            to={`/admin/jobs/${app.job_id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                          >
                            Job: {app.job_id.slice(0, 8)}...
                          </Link>
                          <Link
                            to={`/admin/jobs/${app.job_id}/applications`}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium min-h-[44px] flex items-center"
                          >
                            View Details →
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedApplications.size === filteredApplications.length && filteredApplications.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Application
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applied
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No applications found
                      </td>
                    </tr>
                  ) : (
                    filteredApplications.map((app) => (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <input
                            type="checkbox"
                            checked={selectedApplications.has(app.id)}
                            onChange={() => handleSelectApplication(app.id)}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {app.id.slice(0, 8)}...
                          </div>
                          <div className="text-sm text-gray-500">
                            Candidate: {app.user_id.slice(0, 8)}...
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Link 
                            to={`/admin/jobs/${app.job_id}`}
                            className="text-sm text-primary-600 hover:text-primary-800"
                          >
                            {app.job_id.slice(0, 8)}...
                          </Link>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[app.status] || 'bg-gray-100 text-gray-800'}`}>
                            {app.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          {app.applied_at ? new Date(app.applied_at).toLocaleDateString() : '-'}
                        </td>
                        <td className="px-6 py-4">
                          <Link
                            to={`/admin/jobs/${app.job_id}/applications`}
                            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
                          >
                            View Details
                          </Link>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Conversion Funnel */}
        {analytics?.conversion_rates && (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Conversion Funnel</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-blue-600">
                  {(analytics.conversion_rates.submitted_to_reviewed * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Submitted → Reviewed</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-purple-600">
                  {(analytics.conversion_rates.reviewed_to_interviewed * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Reviewed → Interview</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-green-600">
                  {(analytics.conversion_rates.interviewed_to_offered * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Interview → Offered</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-emerald-600">
                  {(analytics.conversion_rates.offered_to_hired * 100).toFixed(1)}%
                </div>
                <div className="text-sm text-gray-600">Offered → Hired</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
